import { buttonStyle } from 'components/buttons/buttonStyle';
import { Button } from 'components/ui/button';
import { WarningAmberOutlined } from 'components/icons';
import { Checkbox } from 'components/ui/checkbox';
import { Dialog, DialogContent, DialogFooter, DialogTitle } from 'components/ui/dialog';
import { FC, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { t } from 'utils/getTranslations';

/** The confirmation dialog of a send to an external integration (Netvisor or Maventa). The texts come from `messageIds`. */
export interface DialogMessageIds {
    title: string;
    message: string;
    attachSpec: string;
    specUnavailable: string;
    send: string;
}

export const NETVISOR_DIALOG_MESSAGE_IDS: DialogMessageIds = {
    title: 'invoices.netvisor.confirm-dialog.title',
    message: 'invoices.netvisor.confirm-dialog.message',
    attachSpec: 'invoices.netvisor.confirm-dialog.attach-spec',
    specUnavailable: 'invoices.netvisor.confirm-dialog.spec-unavailable',
    send: 'invoices.netvisor.confirm-dialog.send',
};

interface Props {
    open: boolean;
    onClose: () => void;
    onConfirm: (attachSpec: boolean) => void;
    invoiceNumber: string;
    isSpecAvailable: boolean;
    isSending: boolean;
    /** Message ids of the dialog texts. */
    messageIds: DialogMessageIds;
    /** Something the user should know before sending; the send is still allowed. */
    warning?: string;
    /** Whether the specification checkbox starts checked each time the dialog opens, when a specification is available. */
    defaultAttachSpec?: boolean;
}

const SendToIntegrationDialog: FC<Props> = ({
    open,
    onClose,
    onConfirm,
    invoiceNumber,
    isSpecAvailable,
    isSending,
    messageIds,
    warning,
    defaultAttachSpec = false,
}) => {
    const initialAttachSpec = defaultAttachSpec && isSpecAvailable;
    const [attachSpec, setAttachSpec] = useState(initialAttachSpec);
    const intl = useIntl();

    // Reset checkbox to its starting state when `open` or `isSpecAvailable` props change such that the dialog opens or
    // the spec becomes unavailable. Render-time prev-value compare
    // (https://react.dev/reference/react/useState#storing-information-from-previous-renders).
    const [prevOpen, setPrevOpen] = useState(open);
    const [prevIsSpecAvailable, setPrevIsSpecAvailable] = useState(isSpecAvailable);
    if (open !== prevOpen || isSpecAvailable !== prevIsSpecAvailable) {
        setPrevOpen(open);
        setPrevIsSpecAvailable(isSpecAvailable);
        if (open || !isSpecAvailable) {
            setAttachSpec(initialAttachSpec);
        }
    }

    const handleSend = () => {
        onConfirm(attachSpec);
    };

    const handleClose = () => {
        setAttachSpec(initialAttachSpec);
        onClose();
    };

    return (
        <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
            <DialogContent showCloseButton={false} className="gap-5 sm:max-w-[444px]">
                <DialogTitle className="text-base leading-6 font-medium">
                    <FormattedMessage id={messageIds.title} />
                </DialogTitle>
                <div>
                    <p className="text-body1 mb-4">
                        {intl.formatMessage({ id: messageIds.message }, { invoiceNumber })}
                    </p>
                    <label className="text-body1 flex w-fit items-center gap-2.5">
                        <Checkbox
                            checked={attachSpec}
                            onCheckedChange={(checked) => setAttachSpec(checked)}
                            disabled={!isSpecAvailable}
                        />
                        {intl.formatMessage({ id: messageIds.attachSpec })}
                    </label>
                    {!isSpecAvailable && (
                        <span className="text-caption text-fg-secondary ml-6.5 block">
                            <FormattedMessage id={messageIds.specUnavailable} />
                        </span>
                    )}
                    {warning && (
                        <p className="text-body1 mt-4 flex gap-2" data-testid="send-warning">
                            <WarningAmberOutlined className="text-warning mt-0.5 size-4 shrink-0" />
                            {warning}
                        </p>
                    )}
                </div>
                <DialogFooter>
                    <Button {...buttonStyle({ variant: 'text' })} onClick={handleClose} disabled={isSending}>
                        <FormattedMessage id="cancel" />
                    </Button>
                    <Button
                        {...buttonStyle({ variant: 'contained', color: 'secondary' })}
                        onClick={handleSend}
                        disabled={isSending}
                        data-testid="send-button"
                    >
                        {isSending ? (
                            <span
                                role="progressbar"
                                aria-label={t('loading')}
                                className="size-5 animate-spin rounded-full border-2 border-current border-t-transparent"
                            />
                        ) : (
                            <FormattedMessage id={messageIds.send} />
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default SendToIntegrationDialog;
